import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { RefreshCw, Send, Sparkles, CheckCircle2, XCircle, Inbox, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import OfferCard from "@/components/OfferCard";
import OffersMap from "@/components/OffersMap";
import EmptyState from "@/components/EmptyState";
import { Skeleton } from "@/components/ui/skeleton";

const StatCard = ({ label, value, accent, icon: Icon, testid }) => (
  <div
    data-testid={testid}
    className="rounded-xl border border-slate-800 bg-slate-900/50 p-5 hover:border-slate-700 transition-colors"
  >
    <div className="flex items-center justify-between">
      <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-bold">{label}</div>
      <Icon className={`w-4 h-4 ${accent || "text-slate-500"}`} />
    </div>
    <div className={`font-display mt-3 text-3xl font-semibold ${accent || "text-slate-100"}`}>{value}</div>
  </div>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [offers, setOffers] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [sending, setSending] = useState(false);

  const load = async () => {
    try {
      const [s, o, p] = await Promise.all([
        api.get("/offers/stats"),
        api.get("/offers", { params: { matched: true, limit: 100 } }),
        api.get("/profile"),
      ]);
      setStats(s.data);
      setOffers(o.data);
      setProfile(p.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const scan = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/scan");
      toast.success(`Skanowanie zakończone — ${data.new_offers} nowych ofert`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setScanning(false);
    }
  };
  const sendDigest = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/send-digest");
      if (data.success) toast.success(`Wysłano ${data.count} ofert na maila`);
      else toast.error(data.error || "Wysyłka nie powiodła się");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSending(false);
    }
  };

  const changeStatus = async (offer, status) => {
    try {
      await api.patch(`/offers/${offer.id}/status`, { status });
      setOffers((prev) => prev.map((o) => (o.id === offer.id ? { ...o, status } : o)));
      toast.success("Status zaktualizowany");
      const s = await api.get("/offers/stats");
      setStats(s.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="p-8 lg:p-12 max-w-[1600px]">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-cyan-400 font-bold mb-2">
            Panel · Mazowieckie
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-slate-50">
            Dopasowane oferty
          </h1>
          <p className="text-slate-400 mt-2 text-sm">
            Skanowanie automatyczne co 6h · Digest codziennie o 8:00
          </p>
        </div>
        <div className="flex gap-3">
          <motion.button
            whileTap={{ scale: 0.97 }}
            onClick={scan}
            disabled={scanning}
            data-testid="scan-now-button"
            className="inline-flex items-center gap-2 px-4 h-11 rounded-lg border border-slate-700 bg-slate-900/60 hover:border-cyan-400/50 hover:text-cyan-300 text-sm text-slate-200 transition-colors"
          >
            {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Skanuj teraz
          </motion.button>
          <motion.button
            whileTap={{ scale: 0.97 }}
            onClick={sendDigest}
            disabled={sending}
            data-testid="send-digest-button"
            className="inline-flex items-center gap-2 px-4 h-11 rounded-lg bg-cyan-400 hover:bg-cyan-300 text-slate-950 text-sm font-semibold transition-colors"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Wyślij digest
          </motion.button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {loading ? (
          <>
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-[110px] bg-slate-900/60" />
            ))}
          </>
        ) : (
          <>
            <StatCard testid="stat-new" label="Nowe" value={stats?.new || 0} accent="text-cyan-400" icon={Sparkles} />
            <StatCard testid="stat-matched" label="Dopasowane" value={stats?.matched || 0} icon={Inbox} />
            <StatCard
              testid="stat-applied"
              label="Aplikowane"
              value={stats?.applied || 0}
              accent="text-emerald-400"
              icon={CheckCircle2}
            />
            <StatCard
              testid="stat-rejected"
              label="Odrzucone"
              value={stats?.rejected || 0}
              accent="text-red-400"
              icon={XCircle}
            />
          </>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 h-[520px]" data-testid="map-container">
          <OffersMap offers={offers} profile={profile} />
        </div>
        <div className="lg:col-span-7">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl font-medium text-slate-100">Feed dopasowanych</h2>
            <span className="text-xs text-slate-500">{offers.length} ofert</span>
          </div>
          {loading ? (
            <div className="space-y-3">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-[180px] bg-slate-900/60" />
              ))}
            </div>
          ) : offers.length === 0 ? (
            <EmptyState
              testid="dashboard-empty"
              title="Brak dopasowanych ofert"
              description="Uzupełnij profil (adres, przedmioty, promień km) albo kliknij „Skanuj teraz”."
            />
          ) : (
            <motion.div
              variants={{ show: { transition: { staggerChildren: 0.04 } } }}
              initial="hidden"
              animate="show"
              className="space-y-3"
            >
              {offers.map((o) => (
                <motion.div
                  key={o.id}
                  variants={{ hidden: { opacity: 0, y: 8 }, show: { opacity: 1, y: 0 } }}
                >
                  <OfferCard offer={o} onStatusChange={changeStatus} />
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
